import { breakpoints } from '@99designs/common/core/settings';

export type ResponsiveProp<T> = T | Readonly<[T, T]> | Readonly<[T, T, T]> | Readonly<[T, T, T, T]>;

export function normalizeResponsiveProp<T extends string | number>(
    value: ResponsiveProp<T>
): Readonly<[T, T, T, T]> {
    if (typeof value === 'string' || typeof value === 'number') {
        return [value, value, value, value];
    }

    if ('length' in value) {
        const { length } = value;

        if (length == 2) {
            const [smallValue, normalValue] = value as Readonly<[T, T]>;
            return [smallValue, smallValue, normalValue, normalValue];
        }

        if (length == 3) {
            const [smallValue, mediumValue, normalValue] = value as Readonly<[T, T, T]>;
            return [smallValue, mediumValue, normalValue, normalValue];
        }

        if (length == 4) {
            return value as Readonly<[T, T, T, T]>;
        }
    }

    throw new Error(`Invalid responsive prop value: ${JSON.stringify(value)}`);
}

export function resolveResponsiveProp<T extends string | number>(
    value: ResponsiveProp<T>,
    propName: string,
    propValues: Record<T, string>
): string {
    const [smallValue, mediumValue, normalValue, largeValue] = normalizeResponsiveProp(value);

    let css = `${propName}: ${propValues[normalValue]};`;

    // Tablet is different from both Desktop and Mobile
    if (mediumValue != normalValue && mediumValue != smallValue) {
        css += `
            @media (min-width: ${breakpoints.medium[0]}) and (max-width: ${
            breakpoints.medium[1]
        }px) {
                ${propName}: ${propValues[mediumValue]};
            }`;
    }

    // Mobile is different from both Tablet and Desktop
    if (smallValue != mediumValue && smallValue != normalValue) {
        css += `
            @media (min-width: ${breakpoints.small[0]}) and (max-width: ${breakpoints.small[1]}px) {
                ${propName}: ${propValues[smallValue]};
            }`;
    }

    // Tablet and Mobile are the same
    if (mediumValue != normalValue && mediumValue == smallValue) {
        css += `
            @media (min-width: ${breakpoints.small[0]}) and (max-width: ${
            breakpoints.medium[1]
        }px) {
                ${propName}: ${propValues[mediumValue]};
            }`;
    }

    // Large value different to desktop
    if (largeValue != normalValue) {
        css += `
            @media (min-width: ${breakpoints.large[0]}) {
                ${propName}: ${propValues[largeValue]};
            }`;
    }

    return css;
}
